import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import EmployeeCornerSidebar from "./EmployeeCornerSidebar";
import { useLocation,useNavigate } from "react-router-dom";

const formatDDMMYYYY = (date) => {
  if (!date) return "";
  if (typeof date === 'string' && date.includes('-')) {
    const parts = date.split('-');
    if (parts[0].length === 4) { // YYYY-MM-DD
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  // This correctly parses "DD-MM-YYYY" into a Local Date object
  const [d, m, y] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d); // Using year, monthIndex, day constructor uses LOCAL time
};

const normalizeLeaveType = (value = "") => String(value || "").trim().toUpperCase();

const isSickLeave = (value = "") => {
  const type = normalizeLeaveType(value);
  return type === "SL" || type.includes("SICK");
};

const isCasualLeave = (value = "") => {
  const type = normalizeLeaveType(value);
  return type === "CL" || type.includes("CASUAL");
};

const isEarnedLeave = (value = "") => {
  const type = normalizeLeaveType(value);
  return type === "EL" || type.includes("EARNED");
};

const EL_MAX_BALANCE = 24;
const EL_POLICY_START_DATE = new Date(2026, 3, 1);

const getFinancialYearData = (inputDate) => {
  const date = inputDate ? new Date(inputDate) : new Date();
  const month = date.getMonth();
  const year = date.getFullYear();
  const fyStartYear = month < 3 ? year - 1 : year;
  const fyEndYear = fyStartYear + 1;
  const startDate = new Date(fyStartYear, 3, 1);
  const endDate = new Date(fyEndYear, 2, 31);

  return {
    min: `${fyStartYear}-04-01`,
    max: `${fyEndYear}-03-31`,
    startDate,
    endDate,
    sessionName: `${fyStartYear}-${fyEndYear}`
  };
};

const getDaysInFinancialYear = (inputDate) => {
  const { startDate, endDate } = getFinancialYearData(inputDate);
  return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
};

const getElapsedFinancialYearDays = (inputDate) => {
  const { startDate, endDate } = getFinancialYearData(inputDate);
  const date = inputDate ? new Date(inputDate) : new Date();
  const cappedDate = date > endDate ? endDate : date;
  if (cappedDate < startDate) return 0;
  return Math.floor((cappedDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
};

const getElapsedFinancialYearMonths = (inputDate) => {
  const { startDate, endDate } = getFinancialYearData(inputDate);
  const date = inputDate ? new Date(inputDate) : new Date();
  const cappedDate = date > endDate ? endDate : date;
  if (cappedDate < startDate) return 0;
  return (
    (cappedDate.getFullYear() - startDate.getFullYear()) * 12 +
    (cappedDate.getMonth() - startDate.getMonth()) +
    1
  );
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const getMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const buildMonthlyUsageMap = (leaveApplications = [], referenceDate) => {
  const cappedReferenceDate = new Date(referenceDate);
  return leaveApplications.reduce((acc, leave) => {
    const leaveDate = new Date(leave.fromDate);
    if (Number.isNaN(leaveDate.getTime()) || leaveDate > cappedReferenceDate) return acc;
    const monthKey = getMonthKey(leaveDate);
    acc[monthKey] = (acc[monthKey] || 0) + (Number(leave.noOfDays) || 0);
    return acc;
  }, {});
};

const getEarnedLeaveBalance = ({
  totalAllowed,
  referenceDate,
  leaveApplications = [],
}) => {
  const numericAllowed = Number(totalAllowed) || 0;
  if (numericAllowed <= 0) return 0;

  const parsedReferenceDate = new Date(referenceDate);
  if (Number.isNaN(parsedReferenceDate.getTime()) || parsedReferenceDate < EL_POLICY_START_DATE) {
    return 0;
  }

  const monthlyAccrual = numericAllowed / 12;
  const usageByMonth = buildMonthlyUsageMap(leaveApplications, referenceDate);
  
  // Track balance by Financial Year
  let fyBuckets = {}; 
  
  let currentMonth = startOfMonth(EL_POLICY_START_DATE);
  const referenceMonth = startOfMonth(parsedReferenceDate);

  while (currentMonth <= referenceMonth) {
    const fyData = getFinancialYearData(currentMonth);
    const fyKey = fyData.sessionName;

    // 1. Add Accrual to the current FY bucket
    fyBuckets[fyKey] = (fyBuckets[fyKey] || 0) + monthlyAccrual;

    // 2. Subtract Usage from the oldest buckets first (FIFO)
    let usage = usageByMonth[getMonthKey(currentMonth)] || 0;
    const sortedFYs = Object.keys(fyBuckets).sort(); 
    
    for (let fy of sortedFYs) {
      if (usage <= 0) break;
      const deduct = Math.min(fyBuckets[fy], usage);
      fyBuckets[fy] -= deduct;
      usage -= deduct;
    }

    // 3. THE COLLAPSE LOGIC: If total > 24, delete the oldest FY entirely
    let totalBalance = Object.values(fyBuckets).reduce((a, b) => a + b, 0);
    
    while (totalBalance > EL_MAX_BALANCE) {
      const oldestFY = Object.keys(fyBuckets).sort()[0];
      totalBalance -= fyBuckets[oldestFY];
      delete fyBuckets[oldestFY]; // This "collapses" the old year
    }

    currentMonth = addMonths(currentMonth, 1);
  }

  const finalBalance = Object.values(fyBuckets).reduce((a, b) => a + b, 0);
  return Math.floor(finalBalance);
};

const getAccruedLeaveAllowance = (leaveType, totalAllowed, inputDate) => {
  const numericAllowed = Number(totalAllowed) || 0;
  if (numericAllowed <= 0) return 0;

  if (isSickLeave(leaveType)) {
    return numericAllowed;
  }

  if (isCasualLeave(leaveType)) {
    const elapsedDays = getElapsedFinancialYearDays(inputDate);
    const daysInFy = getDaysInFinancialYear(inputDate);
    const cycleDays = daysInFy / numericAllowed;
    const accrued = Math.ceil(elapsedDays / cycleDays);
    return Math.min(numericAllowed, accrued);
  }

  if (isEarnedLeave(leaveType)) {
    return Math.min(numericAllowed, getElapsedFinancialYearMonths(inputDate));
  }

  return numericAllowed;
};

const EmployeeLeaveApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingData = location.state?.editingData || null;
  const [empStatus, setEmpStatus] = useState(""); 
  const [statusChangeDate, setStatusChangeDate] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);


  const loggedUser = JSON.parse(localStorage.getItem("employeeUser")) || {};
  const token = localStorage.getItem("employeeToken");

  const [formData, setFormData] = useState({
    employeeId: loggedUser.employeeID || "",
    employeeUserId: loggedUser.employeeUserId || "",
    employeeName: `${loggedUser.firstName || ""} ${loggedUser.lastName || ""}`.trim(),
    applicationDate: new Date().toISOString().split("T")[0],
    leaveType: "",
    leaveInHand: 0,
    fromDate: "",
    toDate: "",
    noOfDays: 0,
    reason: "",
    reportingManagerEmployeeUserId: "",
    departmentHeadEmployeeUserId: ""
  });

  const formatDate = (date) => (date ? new Date(date).toISOString().split("T")[0] : "");

useEffect(() => {
  if (!loggedUser?.employeeID) return;

// STEP A: Fetch Profile & Employment Status
axios
  .get(`/api/employee-ids/details/${loggedUser.employeeID}`)
  .then((profileRes) => {
    const emp = profileRes.data;
    setEmpStatus(emp.employmentStatus || "");
    setStatusChangeDate(emp.statusChangeDate);
    
    const fullName = `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`.trim();
    
    setFormData((prev) => ({ 
      ...prev, 
      employeeName: fullName,
      // CRITICAL FIX: This ensures employeeUserId (e.g., EMP8) is added to the form
      employeeUserId: emp.employeeUserId || prev.employeeUserId 
    }));
  })
  .catch((err) => console.error("Profile Fetch Error:", err));

  
  // STEP C: Fetch Leave Types
  axios
    .get(`/api/employee-ids/leave-types`)
    .then((res) => setLeaveTypes(res.data || []));

  // STEP D: Prefill Data for Editing
  if (editingData) {
    setIsEditMode(true);
    setEditingId(editingData._id);
    
    setFormData({
      employeeId: editingData.employeeId,
      employeeName: editingData.employeeName,
      applicationDate: formatDate(editingData.applicationDate), 
      leaveType: editingData.leaveType,
      leaveInHand: editingData.leaveInHand,
      fromDate: editingData.fromDate, 
      toDate: editingData.toDate,
      noOfDays: editingData.noOfDays,
      reason: editingData.reason || "",
      reportingManagerEmployeeUserId: editingData.reportingManagerEmployeeUserId || "",
      departmentHeadEmployeeUserId: editingData.departmentHeadEmployeeUserId || ""
    });
  }
}, [loggedUser.employeeID, editingData]);

useEffect(() => {
  if (!formData.employeeUserId) return;

axios
  .get(`/api/leave-application/employee/${formData.employeeUserId}`)
  .then((res) => {
    const allLeaves = res.data || [];
    const otherLeaves = editingData ? allLeaves.filter(l => l._id !== editingData._id) : allLeaves;
    const eligibleLeaves = otherLeaves.filter(
      (leave) => leave.approveRejectedStatus === "APPROVED" || leave.approveRejectedStatus === null
    );
    setLeaveHistory(eligibleLeaves);
  })
    .catch((err) => console.error("History Fetch Error:", err));
}, [formData.employeeUserId, editingData]);

const getRemainingLeaveBalance = (leaveTypeValue, referenceDate = formData.applicationDate) => {
  const master = leaveTypes.find((lt) =>
    normalizeLeaveType(lt.leaveName) === normalizeLeaveType(leaveTypeValue) ||
    normalizeLeaveType(lt.leaveCode) === normalizeLeaveType(leaveTypeValue)
  );

  if (!master) return 0;

  const totalAllowed = Number(master.totalDays) || 0;
  const leaveCode = normalizeLeaveType(master.leaveCode || leaveTypeValue);
  const normalizedReferenceDate = referenceDate || new Date().toISOString().split("T")[0];
  const matchedHistory = leaveHistory.filter((leave) => {
    const currentType = normalizeLeaveType(leave.leaveType);
    return (
      currentType === normalizeLeaveType(master.leaveName) ||
      currentType === normalizeLeaveType(master.leaveCode)
    );
  });

  const accruedAllowed = isEarnedLeave(leaveCode)
    ? getEarnedLeaveBalance({
        totalAllowed,
        referenceDate: normalizedReferenceDate,
        leaveApplications: matchedHistory,
      })
    : getAccruedLeaveAllowance(leaveCode, totalAllowed, normalizedReferenceDate);
  const appDate = new Date(normalizedReferenceDate);
  const fyYear = appDate.getMonth() < 3 ? appDate.getFullYear() - 1 : appDate.getFullYear();
  const fyStart = new Date(fyYear, 3, 1);
  const fyEnd = new Date(fyYear + 1, 2, 31);

  const usedDays = matchedHistory.reduce((total, leave) => {
    const leaveDate = new Date(leave.fromDate);
    if (Number.isNaN(leaveDate.getTime()) || leaveDate < fyStart || leaveDate > fyEnd) {
      return total;
    }
    return total + (Number(leave.noOfDays) || 0);
  }, 0);

  return Math.max(0, accruedAllowed - usedDays);
};

useEffect(() => {
  if (!formData.leaveType || leaveTypes.length === 0) return;
  const calculatedInHand = getRemainingLeaveBalance(formData.leaveType, formData.applicationDate);
  setFormData(prev => ({ ...prev, leaveInHand: calculatedInHand }));
}, [formData.leaveType, formData.applicationDate, leaveHistory, leaveTypes, empStatus, statusChangeDate]);


  // Auto-Calculate Days
  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
     const from = parseDDMMYYYY(formData.fromDate);
      const to = parseDDMMYYYY(formData.toDate);

      if (to >= from) {
        const diff = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
        setFormData((prev) => ({ ...prev, noOfDays: diff }));
      } else {
        setFormData((prev) => ({ ...prev, noOfDays: 0 }));
      }
    }
  }, [formData.fromDate, formData.toDate]);

const handleLeaveTypeChange = (value) => {
  const calculatedInHand = getRemainingLeaveBalance(value, formData.applicationDate);

  setFormData((prev) => ({
    ...prev,
    leaveType: value,
    leaveInHand: calculatedInHand,
  }));
};


const fy = getFinancialYearData(formData.applicationDate);

const handleSubmit = async () => {
  if (formData.noOfDays <= 0) return toast.error("Invalid leave duration");
  
  // A. Check if they have enough balance (already in your code)
  if (formData.noOfDays > formData.leaveInHand) {
    return toast.error(`Insufficient balance. You only have ${formData.leaveInHand} days available.`);
  }

  const type = normalizeLeaveType(formData.leaveType);
  const isCasualLeaveType = isCasualLeave(type);
  
  if (isCasualLeaveType && formData.noOfDays > 5) {
    return toast.error("For Casual Leave, you can only apply for a maximum of 5 days at a time.");
  }
    
  if (formData.leaveInHand === 0) return toast.error("You are not eligible for this leave type");

  // ADD THIS HELPER HERE: Converts "15-01-2026" back to "2026-01-15" for database
  const toISO = (dateStr) => {
    if (!dateStr || !dateStr.includes("-")) return dateStr;
    const parts = dateStr.split("-");
    if (parts[0].length === 4) return dateStr; // Already YYYY-MM-DD
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const dataToSubmit = {
    employeeId: formData.employeeId,
    employeeUserId: formData.employeeUserId,
    employeeName: formData.employeeName,
    applicationDate: formData.applicationDate, 
    leaveType: formData.leaveType,
    leaveInHand: formData.leaveInHand,
    // REPLACE THESE TWO LINES:
    fromDate: toISO(formData.fromDate),
    toDate: toISO(formData.toDate),
    noOfDays: formData.noOfDays,
    reason: formData.reason ? formData.reason.trim() : "",
    reportingManagerEmployeeUserId: formData.reportingManagerEmployeeUserId,
    departmentHeadEmployeeUserId: formData.departmentHeadEmployeeUserId
  };

  try {
    const url = isEditMode 
      ? `/api/leave-application/${editingId}`
      : "/api/leave-application";
    
    await axios[isEditMode ? "put" : "post"](url, dataToSubmit, {
      headers: { Authorization: `Bearer ${token}` }
    });

    toast.success("Submitted successfully!");
    navigate("/EmployeeHome");
  } catch (error) {
    toast.error(error.response?.data?.message || "Submission failed");
  }
};

  const inputClass =
  "w-full pl-2 pr-2 py-1.5 sm:py-1 border border-gray-300 rounded text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-500 transition-all duration-150";

  return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
        <EmployeeCornerSidebar />
        
        {/* Add mt-16 or similar if your sidebar is fixed on mobile to avoid overlap */}
        <div className="flex-1 p-2 pb-24 sm:p-3 sm:pb-3 md:p-6 md:pb-6"> 
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-lg w-full mx-auto">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 border-b pb-2">
            {isEditMode ? "Edit Leave Application" : "New Leave Application"}
          </h2>

          {/* Status Alert for No-Leave categories */}
          {["TP", "PB", "PDB"].includes(empStatus?.toUpperCase()) && (
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs sm:text-sm italic">
              Note: Status "{empStatus}" does not have any allocated leaves.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700">Employee ID</label>
              <input type="text" className={`${inputClass} bg-gray-50`} value={formData.employeeId} readOnly />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700">Employee Name</label>
              <input type="text" className={`${inputClass} bg-gray-100`} value={formData.employeeName || "Loading..."} readOnly />
            </div>

          {/* <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700">Application Date</label>
          
            <input 
              type="date" 
              id="appDatePicker"
              className="hidden" 
              value={formData.applicationDate} 
              onChange={(e) => {
                setFormData(prev => ({ 
                  ...prev, 
                  applicationDate: e.target.value,
                  fromDate: "", 
                  toDate: "" 
                }));
              }}
            />

            <input
              type="text"
              readOnly
              className={inputClass}
              value={formatDDMMYYYY(formData.applicationDate)} // Displays with slashes
              onClick={() => document.getElementById("appDatePicker").showPicker()}
            />
          </div> */}

          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700">Application Date</label>
            <input 
              type="date" 
              id="appDatePicker"
              className="hidden" 
              value={formData.applicationDate} 
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  applicationDate: e.target.value,
                  fromDate: "",
                  toDate: "",
                }))
              }
            />
            <input
              type="text"
              readOnly
              className={`${inputClass} cursor-pointer bg-white`}
              value={formatDDMMYYYY(formData.applicationDate)} 
              onClick={() => document.getElementById("appDatePicker").showPicker()}
            />
          </div>

          {/* <div>
  <label className="block text-xs sm:text-sm font-bold text-gray-700">
    Application Date
  </label>

  <input
    type="date"
    id="appDatePicker"
    className="hidden"
    value={formData.applicationDate}
    onChange={(e) =>
      setFormData({ ...formData, applicationDate: e.target.value })
    }
  />


  <input
    type="text"
    readOnly
    className={`${inputClass} cursor-pointer bg-white`}
    value={formatDDMMYYYY(formData.applicationDate)}
    onClick={() =>
      document.getElementById("appDatePicker").showPicker()
    }
  />
</div> */}
            <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700">Leave Type</label>
            <select 
              className={inputClass} 
              value={formData.leaveType} 
              onChange={(e) => handleLeaveTypeChange(e.target.value)}
            >
              <option value="">-- Select Leave --</option>
              {leaveTypes.map((lt) => {
                const name = lt.leaveName?.toUpperCase();
                const code = lt.leaveCode?.toUpperCase();
                
                const isSick = name.includes("SICK") || code === "SL";
                const isCasualOrEarned = isCasualLeave(code) || isEarnedLeave(code);
                let isEligible = true;

                let hasBalance = true;
                let reason = "";
                const currentBalance = getRemainingLeaveBalance(code, formData.applicationDate);

                if (isSick) {
                  hasBalance = currentBalance > 0;
                  reason = currentBalance > 0 ? "" : "(Limit Reached)";
                } else if (isCasualOrEarned) {
                  hasBalance = currentBalance > 0;
                  reason = currentBalance > 0 ? "" : "(Limit Reached)";
                }

            return (
              <option 
                key={lt._id} 
                value={lt.leaveName} 
                disabled={!isEligible || !hasBalance}
                className={!hasBalance || !isEligible ? "text-gray-400 bg-gray-100" : "text-black"}
              >
                {lt.leaveName} {reason} {!isEligible ? "(Ineligible)" : ""}
              </option>
            );
          })}
        </select>
      </div>


          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700">From Date</label>
            <input
              type="date"
              className="hidden"
              id="fromDatePicker"
              min={fy.min} // e.g., "2025-04-01"
              max={fy.max} // e.g., "2026-03-31"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  fromDate: formatDDMMYYYY(e.target.value),
                })
              }
            />
            <input
              type="text"
              readOnly
              className={inputClass}
              value={formData.fromDate}
              onClick={() => document.getElementById("fromDatePicker").showPicker()}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700">To Date</label>
            <input
              type="date"
              className="hidden"
              id="toDatePicker"
              min={fy.min}
              max={fy.max}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  toDate: formatDDMMYYYY(e.target.value),
                })
              }
            />
            <input
              type="text"
              readOnly
              className={inputClass}
              value={formData.toDate}
              onClick={() => document.getElementById("toDatePicker").showPicker()}
            />
          </div>
            <div className="bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-200 text-sm">
              <p className="text-blue-800 font-bold">Total Days: {formData.noOfDays}</p>
              <p className="text-sm text-blue-600">Allocation: {formData.leaveInHand} Days</p>
              {formData.noOfDays > 5 && isCasualLeave(formData.leaveType) && (
              <p className="text-[10px] text-red-600 font-bold mt-1 bg-red-50 p-1 rounded border border-red-200">
                ⚠️ Limit: Max 5 days per application
              </p>
            )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-xs sm:text-sm font-bold text-gray-700">Reason (Optional)</label>
            <textarea className={`${inputClass} h-16 sm:h-12 p-2`} value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            {/* <button className="bg-gray-400 text-white px-6 py-2 rounded-lg">Cancel</button> */}
            <button 
              onClick={handleSubmit}
              // disabled={["TP", "PB", "PDB"].includes(empStatus?.toUpperCase())}
              className="disabled:bg-gray-300 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-2 rounded-lg font-bold transition-colors"
            >
              {isEditMode ? "Update Application" : "Submit Application"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLeaveApplication;
